import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, InputField, LinkButton } from '@enamelai/design-system'
import { IClinicNumber } from '@dataTypes/clinic.type'
import { useAppDispatch } from '@store/hooks'
import { sendPortOtpAction } from '@store/actions/settings-communication.action'

interface OTPVerificationProps {
  numberData: IClinicNumber
  handleChange: (field: string, value: string | boolean | null) => void
  currentPhoneNumber: string
  currentIndex: number
  totalCount: number
  setOtp: Dispatch<SetStateAction<string | null>>
  validationErrors: { [key: string]: string }
}

const OTPVerification: React.FC<OTPVerificationProps> = ({
  currentPhoneNumber,
  currentIndex,
  totalCount,
  setOtp,
  validationErrors,
}) => {
  const dispatch = useAppDispatch()
  const [method, setMethod] = useState<'sms' | 'call' | null>(null)
  const [otpValue, setOtpValue] = useState('')
  const lastSubmittedOtpRef = useRef('')

  const triggerSendOtp = (channel: 'sms' | 'call'): void => {
    if (!currentPhoneNumber.trim()) return

    setMethod(channel)
    setOtpValue('')
    lastSubmittedOtpRef.current = ''
    setOtp(null)
    void dispatch(
      sendPortOtpAction({
        phoneNumber: currentPhoneNumber.trim(),
        channel,
      }),
    )
  }

  const handleResend = (): void => {
    triggerSendOtp(method === 'call' ? 'call' : 'sms')
  }

  const alternateMethodLabel =
    method === 'call' ? 'Send SMS' : 'Call phone number'

  const handleAlternateMethod = (): void => {
    triggerSendOtp(method === 'call' ? 'sms' : 'call')
  }

  useEffect(() => {
    setMethod(null)
    setOtpValue('')
    lastSubmittedOtpRef.current = ''
    setOtp(null)
  }, [currentPhoneNumber, setOtp])

  useEffect(() => {
    if (!method || otpValue.length !== 6) return
    if (!currentPhoneNumber || lastSubmittedOtpRef.current === otpValue) return

    lastSubmittedOtpRef.current = otpValue
    setOtp(otpValue)
  }, [currentPhoneNumber, method, otpValue, setOtp])

  return (
    <div className="scp-pm-port-form">
      {method || true ? (
        <>
          <span className="scp-pm-blue-label">
            ({currentIndex + 1} out of {totalCount}) Enter the code that was
            sent to <strong>{currentPhoneNumber}</strong>.
          </span>
          <InputField
            variant="verification-v2"
            fullWidth
            label="Verification code"
            otpLength={6}
            value={otpValue}
            error={!!validationErrors.is_verified}
            helperText={
              validationErrors.is_verified
                ? 'Invalid OTP. Please try again.'
                : undefined
            }
            onChange={(event) => setOtpValue(event.target.value)}
          />
          <div className="scp-pm-otp-actions secondary">
            <span className="scp-pm-otp-muted">No code received?</span>
            <LinkButton type="button" size="sm" onClick={handleResend}>
              Resend code
            </LinkButton>
            <span className="scp-pm-otp-muted">or</span>
            <LinkButton type="button" size="sm" onClick={handleAlternateMethod}>
              {alternateMethodLabel}
            </LinkButton>
          </div>
        </>
      ) : (
        <>
          <span className="scp-pm-blue-label">
            ({currentIndex + 1} out of {totalCount}) Select verification method
            for <strong>{currentPhoneNumber}</strong>
          </span>
          <div className="scp-pm-full-buttons">
            <Button
              variant="secondary"
              size="md"
              onClick={() => triggerSendOtp('sms')}
            >
              SMS
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => triggerSendOtp('call')}
            >
              Call phone number
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default OTPVerification
